import { useEffect, useState, useCallback, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "universal-cookie";
import jwt_decode from "jwt-decode";
import "./AdminDashboard.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import "moment/locale/es";
import {
  getMe,
  listUsuarios,
  createUsuario,
  updateUsuario,
  deleteUsuario,
  listReservaciones,
  updateReservacion,
  deleteReservacion,
  bulkSaveReservaciones,
  bulkDeleteReservaciones,
  deleteSerieReservaciones,
  listReservacionesSemana,
} from "../requests/admin";

moment.locale("es");
moment.tz.setDefault("America/Mexico_City");
const localizer = momentLocalizer(moment);

const ROLES = ["admin", "director", "video", "staff", "user"];
const SALAS = [
  "Sala VIP",
  "Sala Ejecutiva Comercial",
  "Editorial",
  "Estudio de Podcast",
  "Hall",
  "Estudio",
  "Presidencia",
];
const DIAS = [
  { label: "Lunes", value: 1 },
  { label: "Martes", value: 2 },
  { label: "Miércoles", value: 3 },
  { label: "Jueves", value: 4 },
  { label: "Viernes", value: 5 },
  { label: "Sábado", value: 6 },
  { label: "Domingo", value: 0 },
];

// Convierte un item DynamoDB ({campo:{S:"x"}}) a objeto plano.
function flattenItem(item) {
  const out = {};
  Object.keys(item || {}).forEach((k) => {
    const v = item[k];
    out[k] = v && (v.S !== undefined ? v.S : v.N !== undefined ? v.N : v);
  });
  return out;
}

// Genera un id único para reservaciones.
function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function RoleBadge({ role }) {
  const r = role || "user";
  return <span className={`role-badge role-${r}`}>{r}</span>;
}

// Muestra quién hizo la última acción y, al pasar el mouse, cuándo.
function AuditInfo({ creadoPor, fechaCreacion, modificadoPor, fechaModificacion }) {
  const autor = modificadoPor || creadoPor;
  if (!autor) return <span style={{ color: "#9ca3af" }}>—</span>;
  const fecha = fechaModificacion || fechaCreacion;
  const title = [
    creadoPor && `Creado por ${creadoPor}${fechaCreacion ? " el " + fechaCreacion.replace("T", " ").slice(0, 16) : ""}`,
    modificadoPor && modificadoPor !== creadoPor && `Editado por ${modificadoPor}${fechaModificacion ? " el " + fechaModificacion.replace("T", " ").slice(0, 16) : ""}`,
  ].filter(Boolean).join("\n");
  return (
    <span title={title} style={{ cursor: "help" }}>
      {autor}
      {fecha && (
        <small style={{ display: "block", color: "#9ca3af" }}>
          {fecha.replace("T", " ").slice(0, 16)}
        </small>
      )}
    </span>
  );
}

// Arma y descarga un CSV a partir de un array de objetos planos.
function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) {
    toast.warn("No hay datos para exportar.");
    return;
  }
  const headers = Object.keys(rows[0]);
  const escape = (val) => {
    const s = val === undefined || val === null ? "" : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Pagina hacia adelante automáticamente para juntar TODos los registros antes
// de exportar. Es una acción explícita del admin (botón "Exportar"), no una
// carga automática, por lo que no reintroduce el costo que evita la paginación.
async function fetchAllPages(listFn, extraParams = {}) {
  let lastKey;
  const all = [];
  do {
    const res = await listFn({ limit: 100, lastKey, ...extraParams });
    if (!res.ok) {
      throw new Error((res.data && res.data.message) || "Error al exportar.");
    }
    const body = res.data.body;
    const page = (body.users || body.reservaciones).Items || [];
    all.push(...page.map(flattenItem));
    lastKey = body.nextKey || undefined;
  } while (lastKey);
  return all;
}

const PAGE_SIZE = 10;

// Controles de paginación por cursor (DynamoDB no soporta "ir a página N",
// solo "siguiente" con el LastEvaluatedKey de la página actual).
function PaginationControls({ pageNum, hasPrev, hasNext, onPrev, onNext, loading }) {
  return (
    <div className="admin-pagination" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
      <button
        className="admin-btn admin-btn-ghost admin-btn-sm"
        disabled={!hasPrev || loading}
        onClick={onPrev}
      >
        ← Anterior
      </button>
      <span className="admin-hint" style={{ margin: 0 }}>Página {pageNum}</span>
      <button
        className="admin-btn admin-btn-ghost admin-btn-sm"
        disabled={!hasNext || loading}
        onClick={onNext}
      >
        Siguiente →
      </button>
    </div>
  );
}

function AdminDashboard({ navigate, setSession }) {
  const cookies = new Cookies();
  const [tab, setTab] = useState("usuarios");
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let sessionTimer = null;
    let cancelled = false;

    const checkSession = async () => {
      const token = cookies.get("id_token");
      const tokenExpired = !token || (() => {
        try { return jwt_decode(token).exp * 1000 < Date.now(); }
        catch { return true; }
      })();

      if (tokenExpired) {
        if (cancelled) return;
        toast.warn("Tu sesión expiró. Por favor inicia sesión de nuevo.", { position: "top-center", autoClose: 3000 });
        sessionTimer = setTimeout(() => setSession(false), 3000);
        return;
      }

      const res = await getMe();
      if (cancelled) return;

      if (res.ok && res.data && res.data.user && res.data.user.role === "admin") {
        setAuthorized(true);
      } else if (res.status === 401) {
        // El token luce válido localmente pero el servidor lo rechazó.
        // Cerramos sesión sin toast para evitar el falso positivo justo
        // después de un login exitoso.
        sessionTimer = setTimeout(() => setSession(false), 500);
        return;
      } else {
        setAuthorized(false);
      }
      setChecking(false);
    };

    checkSession();

    return () => {
      cancelled = true;
      if (sessionTimer) clearTimeout(sessionTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (checking) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">Verificando permisos…</div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="admin-dashboard">
        <div className="admin-shell">
          <div className="admin-card" style={{ textAlign: "center" }}>
            <h5>Acceso denegado</h5>
            <p style={{ color: "#6b7280" }}>
              Necesitas rol de administrador para ver este panel.
            </p>
            <button className="admin-btn admin-btn-primary" onClick={() => navigate("/")}>
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <ToastContainer position="top-right" limit={3} autoClose={3000} />
      <div className="admin-shell">
        <div className="admin-header">
          <div>
            <h2>Panel de administración</h2>
            <div className="admin-sub">Gestión de usuarios y reservaciones de salas</div>
          </div>
          <button className="admin-back-btn" onClick={() => navigate("/")}>
            ← Volver a reservaciones
          </button>
        </div>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${tab === "usuarios" ? "active" : ""}`}
            onClick={() => setTab("usuarios")}
          >
            Usuarios
          </button>
          <button
            className={`admin-tab ${tab === "reservaciones" ? "active" : ""}`}
            onClick={() => setTab("reservaciones")}
          >
            Reservaciones
          </button>
          <button
            className={`admin-tab ${tab === "recurrentes" ? "active" : ""}`}
            onClick={() => setTab("recurrentes")}
          >
            Reserva recurrente
          </button>
        </div>

        {tab === "usuarios" && <UsersPanel cookies={cookies} />}
        {tab === "reservaciones" && <ReservationsPanel />}
        {tab === "recurrentes" && <RecurringPanel />}
      </div>
    </div>
  );
}

// =============================== USUARIOS ===================================
function UsersPanel({ cookies }) {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", nombre: "", role: "user" });
  const miEmail = cookies.get("email");

  // Paginación por cursor: pila de lastKeys visitados (para "Anterior") +
  // lastKey de la página actual (para "Siguiente"). Evita escanear toda la
  // tabla de DynamoDB de golpe.
  const [keyStack, setKeyStack] = useState([]);
  const [nextKey, setNextKey] = useState(null);
  const [pageNum, setPageNum] = useState(1);

  // Búsqueda en TODA la tabla (no solo la página cargada), con debounce.
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async (key) => {
    setLoading(true);
    const res = await listUsuarios({ limit: PAGE_SIZE, lastKey: key, q: debouncedQ || undefined });
    if (res.ok && res.data && res.data.body && res.data.body.users) {
      const items = (res.data.body.users.Items || []).map(flattenItem);
      items.sort((a, b) => (a.email || "").localeCompare(b.email || ""));
      setUsuarios(items);
      setNextKey(res.data.body.nextKey || null);
    } else {
      toast.error("No se pudieron cargar los usuarios.");
    }
    setLoading(false);
  }, [debouncedQ]);

  useEffect(() => {
    setKeyStack([]);
    setPageNum(1);
    load(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const onExportCsv = async () => {
    try {
      const all = await fetchAllPages(listUsuarios, debouncedQ ? { q: debouncedQ } : {});
      downloadCsv("usuarios.csv", all);
    } catch (e) {
      toast.error(e.message || "Error al exportar.");
    }
  };

  const goFirstPage = () => {
    setKeyStack([]);
    setPageNum(1);
    load(undefined);
  };
  const goNext = () => {
    if (!nextKey) return;
    setKeyStack((s) => [...s, nextKey]);
    setPageNum((p) => p + 1);
    load(nextKey);
  };
  const goPrev = () => {
    if (keyStack.length === 0) return;
    const stack = keyStack.slice(0, -1);
    const prevKey = stack.length > 0 ? stack[stack.length - 1] : undefined;
    setKeyStack(stack);
    setPageNum((p) => p - 1);
    load(prevKey);
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.warn("El email es obligatorio.");
    const res = await createUsuario(form);
    if (res.ok) {
      toast.success("Usuario creado.");
      setForm({ email: "", nombre: "", role: "user" });
      goFirstPage();
    } else {
      toast.error((res.data && res.data.message) || "Error al crear usuario.");
    }
  };

  const onChangeRole = async (email, role) => {
    const res = await updateUsuario({ email, role });
    if (res.ok) {
      toast.success("Rol actualizado.");
      load(keyStack.length > 0 ? keyStack[keyStack.length - 1] : undefined);
    } else {
      toast.error((res.data && res.data.message) || "Error al actualizar.");
    }
  };

  const onDelete = async (email) => {
    if (!window.confirm(`¿Eliminar al usuario ${email}?`)) return;
    const res = await deleteUsuario(email);
    if (res.ok) {
      toast.success("Usuario eliminado.");
      goFirstPage();
    } else {
      toast.error((res.data && res.data.message) || "Error al eliminar.");
    }
  };

  return (
    <div>
      <div className="admin-card">
        <h5>Agregar usuario</h5>
        <form onSubmit={onCreate}>
          <div className="admin-grid admin-grid-3">
            <div>
              <label className="admin-label">Email</label>
              <input
                type="email"
                className="admin-input"
                placeholder="persona@mmkgroup.com.mx"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label className="admin-label">Nombre</label>
              <input
                type="text"
                className="admin-input"
                placeholder="Nombre completo"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </div>
            <div>
              <label className="admin-label">Rol</label>
              <select
                className="admin-select"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <button className="admin-btn admin-btn-success" type="submit">
              + Crear usuario
            </button>
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h5>Usuarios (página {pageNum}, {usuarios.length} en esta página)</h5>
        <div className="admin-toolbar">
          <input
            className="admin-input admin-search"
            placeholder="🔍 Buscar por email o nombre (en toda la tabla)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="admin-btn admin-btn-ghost" onClick={onExportCsv}>
            Exportar CSV
          </button>
        </div>
        {loading ? (
          <div className="admin-loading">Cargando usuarios…</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Rol</th>
                  <th>Creado/editado por</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.email}>
                    <td>{u.email}</td>
                    <td>{u.nombre}</td>
                    <td>
                      {u.email === miEmail ? (
                        <RoleBadge role={u.role} />
                      ) : (
                        <select
                          className="admin-select"
                          style={{ maxWidth: 160, padding: "6px 10px" }}
                          value={u.role || "user"}
                          onChange={(e) => onChangeRole(u.email, e.target.value)}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td><AuditInfo {...u} /></td>
                    <td className="text-end">
                      <button
                        className="admin-btn admin-btn-danger admin-btn-sm"
                        disabled={u.email === miEmail}
                        onClick={() => onDelete(u.email)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
                {usuarios.length === 0 && (
                  <tr>
                    <td className="admin-empty" colSpan={5}>
                      Sin usuarios.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <PaginationControls
          pageNum={pageNum}
          hasPrev={pageNum > 1}
          hasNext={!!nextKey}
          onPrev={goPrev}
          onNext={goNext}
          loading={loading}
        />
        <p className="admin-hint">
          No puedes cambiar tu propio rol ni eliminar tu propio usuario (protección del
          servidor).
        </p>
      </div>
    </div>
  );
}

// ----- Modal genérico (overlay + card centrada) -----
function Modal({ title, onClose, children, maxWidth = 520 }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="admin-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="admin-modal" style={{ maxWidth }}>
        <div className="admin-modal-header">
          <h5>{title}</h5>
          <button className="admin-modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>
        <div className="admin-modal-body">{children}</div>
      </div>
    </div>
  );
}

// Confirmación en dos pasos para acciones destructivas (eliminar, cancelar serie):
// el primer "Sí" solo avanza a una segunda advertencia; hace falta confirmar
// dos veces para que se dispare onConfirm.
function DoubleConfirmModal({ title, message, confirmLabel, onConfirm, onClose }) {
  const [step, setStep] = useState(1);
  return (
    <Modal title={title} onClose={onClose} maxWidth={440}>
      {step === 1 ? (
        <>
          <p style={{ margin: "0 0 4px", color: "#374151", lineHeight: 1.5 }}>{message}</p>
          <div className="admin-modal-actions">
            <button className="admin-btn admin-btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button className="admin-btn admin-btn-danger" onClick={() => setStep(2)}>
              {confirmLabel}
            </button>
          </div>
        </>
      ) : (
        <>
          <p style={{ margin: "0 0 4px", color: "#991b1b", fontWeight: 600, lineHeight: 1.5 }}>
            ¿Estás completamente seguro? Esta acción no se puede deshacer.
          </p>
          <div className="admin-modal-actions">
            <button className="admin-btn admin-btn-ghost" onClick={onClose}>
              No, cancelar
            </button>
            <button className="admin-btn admin-btn-danger" onClick={onConfirm}>
              Sí, confirmar definitivamente
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ============================ RESERVACIONES =================================
function ReservationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("tabla"); // "tabla" | "calendario"
  const [confirmDelete, setConfirmDelete] = useState(null); // reservación a eliminar, o null
  const [confirmCancelSerie, setConfirmCancelSerie] = useState(null); // { serieId, count } o null

  // Paginación por cursor (ver nota en UsersPanel).
  const [keyStack, setKeyStack] = useState([]);
  const [nextKey, setNextKey] = useState(null);
  const [pageNum, setPageNum] = useState(1);

  // Búsqueda en TODA la tabla (no solo la página cargada), con debounce.
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  // Filtros adicionales: sala exacta, fecha exacta y correo de quien reservó.
  const [salaFilter, setSalaFilter] = useState("");
  const [fechaFilter, setFechaFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEmail(emailFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [emailFilter]);

  const load = useCallback(async (key) => {
    setLoading(true);
    const res = await listReservaciones({
      limit: PAGE_SIZE,
      lastKey: key,
      q: debouncedQ || undefined,
      sala: salaFilter || undefined,
      fecha: fechaFilter || undefined,
      email: debouncedEmail || undefined,
    });
    if (res.ok && res.data && res.data.body && res.data.body.reservaciones) {
      // El backend ya regresa ordenado de la más reciente a la más vieja.
      const list = (res.data.body.reservaciones.Items || []).map(flattenItem);
      setItems(list);
      setSelected({});
      setNextKey(res.data.body.nextKey || null);
    } else {
      toast.error("No se pudieron cargar las reservaciones.");
    }
    setLoading(false);
  }, [debouncedQ, salaFilter, fechaFilter, debouncedEmail]);

  useEffect(() => {
    setKeyStack([]);
    setPageNum(1);
    load(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const goFirstPage = () => {
    setKeyStack([]);
    setPageNum(1);
    load(undefined);
  };
  const goNext = () => {
    if (!nextKey) return;
    setKeyStack((s) => [...s, nextKey]);
    setPageNum((p) => p + 1);
    load(nextKey);
  };
  const goPrev = () => {
    if (keyStack.length === 0) return;
    const stack = keyStack.slice(0, -1);
    const prevKey = stack.length > 0 ? stack[stack.length - 1] : undefined;
    setKeyStack(stack);
    setPageNum((p) => p - 1);
    load(prevKey);
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = (ids, value) => {
    const next = {};
    ids.forEach((id) => (next[id] = value));
    setSelected(next);
  };

  const doDeleteOne = async () => {
    const id = confirmDelete.reservacionId;
    setConfirmDelete(null);
    const res = await deleteReservacion(id);
    if (res.ok) {
      toast.success("Reservación eliminada.");
      goFirstPage();
    } else {
      toast.error("Error al eliminar.");
    }
  };

  const onBulkDelete = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return toast.warn("Selecciona al menos una.");
    if (!window.confirm(`¿Eliminar ${ids.length} reservación(es)?`)) return;
    const res = await bulkDeleteReservaciones(ids);
    if (res.ok) {
      toast.success(`${ids.length} eliminada(s).`);
      goFirstPage();
    } else {
      toast.error((res.data && res.data.message) || "Error en borrado masivo.");
    }
  };

  const onSaveEdit = async () => {
    const res = await updateReservacion(editing);
    if (res.ok) {
      toast.success("Reservación actualizada.");
      setEditing(null);
      load(keyStack.length > 0 ? keyStack[keyStack.length - 1] : undefined);
    } else if (res.status === 409) {
      toast.error("Choque de horario: la sala ya está reservada.");
    } else {
      toast.error((res.data && res.data.message) || "Error al actualizar.");
    }
  };

  const doCancelSerie = async () => {
    const { serieId } = confirmCancelSerie;
    setConfirmCancelSerie(null);
    const res = await deleteSerieReservaciones(serieId);
    if (res.ok) {
      toast.success((res.data && res.data.message) || "Serie cancelada.");
      goFirstPage();
    } else {
      toast.error((res.data && res.data.message) || "Error al cancelar la serie.");
    }
  };

  const onExportCsv = async () => {
    try {
      const extra = {};
      if (debouncedQ) extra.q = debouncedQ;
      if (salaFilter) extra.sala = salaFilter;
      if (fechaFilter) extra.fecha = fechaFilter;
      if (debouncedEmail) extra.email = debouncedEmail;
      const all = await fetchAllPages(listReservaciones, extra);
      downloadCsv("reservaciones.csv", all);
    } catch (e) {
      toast.error(e.message || "Error al exportar.");
    }
  };

  const clearFilters = () => {
    setQ("");
    setSalaFilter("");
    setFechaFilter("");
    setEmailFilter("");
  };
  const hasFilters = q || salaFilter || fechaFilter || emailFilter;

  const itemIds = items.map((r) => r.reservacionId);
  const selectedCount = Object.keys(selected).filter((k) => selected[k]).length;

  return (
    <div className="admin-card">
      <h5>Reservaciones (página {pageNum}, {items.length} en esta página)</h5>
      <div className="admin-tabs" style={{ marginBottom: 14 }}>
        <button
          className={`admin-tab ${view === "tabla" ? "active" : ""}`}
          onClick={() => setView("tabla")}
        >
          Tabla
        </button>
        <button
          className={`admin-tab ${view === "calendario" ? "active" : ""}`}
          onClick={() => setView("calendario")}
        >
          Calendario
        </button>
      </div>

      {view === "calendario" ? (
        <ReservationsCalendarView />
      ) : (
        <>
          <div className="admin-toolbar">
            <input
              className="admin-input admin-search"
              placeholder="🔍 Buscar por asunto o sala (en toda la tabla)…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              className="admin-select"
              style={{ maxWidth: 220 }}
              value={salaFilter}
              onChange={(e) => setSalaFilter(e.target.value)}
            >
              <option value="">Todas las salas</option>
              {SALAS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <input
              type="date"
              className="admin-input"
              style={{ maxWidth: 170 }}
              title="Filtrar por fecha de inicio"
              value={fechaFilter}
              onChange={(e) => setFechaFilter(e.target.value)}
            />
            <input
              type="email"
              className="admin-input"
              style={{ maxWidth: 240 }}
              placeholder="🔍 Correo de quien reservó…"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
            />
            {hasFilters && (
              <button className="admin-btn admin-btn-ghost" onClick={clearFilters}>
                Limpiar filtros
              </button>
            )}
            <button className="admin-btn admin-btn-ghost" onClick={() => load(undefined)}>
              Refrescar
            </button>
            <button className="admin-btn admin-btn-ghost" onClick={onExportCsv}>
              Exportar CSV
            </button>
            <button
              className="admin-btn admin-btn-danger"
              disabled={selectedCount === 0}
              onClick={onBulkDelete}
            >
              Eliminar seleccionadas ({selectedCount})
            </button>
          </div>

          {loading ? (
            <div className="admin-loading">Cargando reservaciones…</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        className="admin-check"
                        type="checkbox"
                        onChange={(e) => toggleAll(itemIds, e.target.checked)}
                      />
                    </th>
                    <th>Sala</th>
                    <th>Asunto</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Creado/editado por</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.reservacionId}>
                      <td>
                        <input
                          className="admin-check"
                          type="checkbox"
                          checked={!!selected[r.reservacionId]}
                          onChange={() => toggle(r.reservacionId)}
                        />
                      </td>
                      <td>{r.sala}</td>
                      <td>{r.asunto}</td>
                      <td>{(r.fechaHoraInicio || "").replace("T", " ")}</td>
                      <td>{(r.fechaHoraFin || "").replace("T", " ")}</td>
                      <td><AuditInfo {...r} /></td>
                      <td className="text-end">
                        <button
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          style={{ marginRight: 8 }}
                          onClick={() => setEditing({ ...r })}
                        >
                          Editar
                        </button>
                        {r.serieId && (
                          <button
                            className="admin-btn admin-btn-ghost admin-btn-sm"
                            style={{ marginRight: 8 }}
                            onClick={() =>
                              setConfirmCancelSerie({
                                serieId: r.serieId,
                                count: items.filter((x) => x.serieId === r.serieId).length,
                              })
                            }
                          >
                            Cancelar serie
                          </button>
                        )}
                        <button
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          onClick={() => setConfirmDelete(r)}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td className="admin-empty" colSpan={7}>
                        Sin reservaciones.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <PaginationControls
            pageNum={pageNum}
            hasPrev={pageNum > 1}
            hasNext={!!nextKey}
            onPrev={goPrev}
            onNext={goNext}
            loading={loading}
          />
        </>
      )}

      {editing && (
        <Modal title="Editar reservación" onClose={() => setEditing(null)} maxWidth={640}>
          <div className="admin-grid admin-grid-2">
            <div>
              <label className="admin-label">Sala</label>
              <select
                className="admin-select"
                value={editing.sala}
                onChange={(e) => setEditing({ ...editing, sala: e.target.value })}
              >
                {SALAS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="admin-label">Asunto</label>
              <input
                className="admin-input"
                value={editing.asunto || ""}
                onChange={(e) => setEditing({ ...editing, asunto: e.target.value })}
              />
            </div>
            <div>
              <label className="admin-label">Inicio</label>
              <input
                type="datetime-local"
                className="admin-input"
                value={(editing.fechaHoraInicio || "").slice(0, 16)}
                onChange={(e) =>
                  setEditing({ ...editing, fechaHoraInicio: e.target.value })
                }
              />
            </div>
            <div>
              <label className="admin-label">Fin</label>
              <input
                type="datetime-local"
                className="admin-input"
                value={(editing.fechaHoraFin || "").slice(0, 16)}
                onChange={(e) =>
                  setEditing({ ...editing, fechaHoraFin: e.target.value })
                }
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="admin-label">Invitados (correos separados por coma)</label>
              <textarea
                className="admin-input"
                rows={2}
                style={{ resize: "vertical" }}
                placeholder="persona@mmkgroup.com.mx, otra.persona@mmkgroup.com.mx"
                value={editing.invitados || ""}
                onChange={(e) => setEditing({ ...editing, invitados: e.target.value })}
              />
            </div>
          </div>
          <div className="admin-modal-actions">
            <button className="admin-btn admin-btn-ghost" onClick={() => setEditing(null)}>
              Cancelar
            </button>
            <button className="admin-btn admin-btn-primary" onClick={onSaveEdit}>
              Guardar cambios
            </button>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <DoubleConfirmModal
          title="Eliminar reservación"
          message={`¿Eliminar la reservación "${confirmDelete.asunto || "(sin asunto)"}" en ${confirmDelete.sala} (${(confirmDelete.fechaHoraInicio || "").replace("T", " ")})?`}
          confirmLabel="Sí, eliminar"
          onConfirm={doDeleteOne}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {confirmCancelSerie && (
        <DoubleConfirmModal
          title="Cancelar serie recurrente"
          message={`¿Cancelar toda la serie? Hay al menos ${confirmCancelSerie.count} ocurrencia(s) en esta página; puede haber más en otras páginas. Se eliminarán TODAS las ocurrencias de esta serie.`}
          confirmLabel="Sí, cancelar serie"
          onConfirm={doCancelSerie}
          onClose={() => setConfirmCancelSerie(null)}
        />
      )}
    </div>
  );
}

// Vista de solo lectura: reutiliza el endpoint /reservaciones/semana (ya
// acotado por rango de fechas, igual de "barato" que el calendario principal
// en Reservations.js) en vez de traer toda la tabla.
// Un calendario por sala (en vez de uno solo con todo mezclado) para que no
// parezca que las reservaciones de salas distintas se empalman entre sí.
function ReservationsCalendarView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [salaFocus, setSalaFocus] = useState("");

  const loadWeek = useCallback(async (d) => {
    setLoading(true);
    const start = moment(d).startOf("week").format("YYYY-MM-DD");
    const end = moment(d).endOf("week").format("YYYY-MM-DD");
    const res = await listReservacionesSemana(start, end);
    if (res.ok && res.data && res.data.body && res.data.body.reservaciones) {
      const list = (res.data.body.reservaciones.Items || []).map(flattenItem);
      setEvents(
        list.map((r) => ({
          title: r.asunto || "(sin asunto)",
          start: moment.tz(r.fechaHoraInicio, "America/Mexico_City").toDate(),
          end: moment.tz(r.fechaHoraFin, "America/Mexico_City").toDate(),
          resource: r,
        }))
      );
    } else {
      toast.error("No se pudieron cargar las reservaciones de la semana.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadWeek(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadWeek]);

  const handleNavigate = (newDate) => {
    setDate(newDate);
    loadWeek(newDate);
  };
  const goPrevWeek = () => handleNavigate(moment(date).subtract(1, "week").toDate());
  const goNextWeek = () => handleNavigate(moment(date).add(1, "week").toDate());
  const goToday = () => handleNavigate(new Date());

  const handleSelectEvent = (event) => {
    const r = event.resource;
    toast.info(
      `${r.sala} — ${r.asunto} — ${(r.fechaHoraInicio || "").replace("T", " ")} a ` +
        `${(r.fechaHoraFin || "").replace("T", " ")}` +
        (r.invitados ? ` — Invitados: ${r.invitados}` : "")
    );
  };

  const salasAMostrar = salaFocus ? [salaFocus] : SALAS;
  const minHour = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 7);
  const maxHour = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23);

  return (
    <div>
      <div className="admin-toolbar" style={{ marginBottom: 14 }}>
        <select
          className="admin-select"
          style={{ maxWidth: 260 }}
          value={salaFocus}
          onChange={(e) => setSalaFocus(e.target.value)}
        >
          <option value="">Todas las salas (un calendario por sala)</option>
          {SALAS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={goPrevWeek}>
          ← Semana anterior
        </button>
        <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={goToday}>
          Hoy
        </button>
        <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={goNextWeek}>
          Semana siguiente →
        </button>
      </div>

      {loading && (
        <div className="admin-hint" style={{ marginBottom: 10, marginTop: 0 }}>
          Cargando semana…
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: salaFocus ? "1fr" : "repeat(auto-fit, minmax(380px, 1fr))",
          gap: 20,
        }}
      >
        {salasAMostrar.map((sala) => (
          <div key={sala}>
            <h6 style={{ marginBottom: 8 }}>{sala}</h6>
            <Calendar
              localizer={localizer}
              events={events.filter((e) => e.resource.sala === sala)}
              date={date}
              view="week"
              views={["week"]}
              toolbar={false}
              onNavigate={handleNavigate}
              onSelectEvent={handleSelectEvent}
              style={{ width: "100%", height: salaFocus ? 550 : 320 }}
              min={minHour}
              max={maxHour}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================ RECURRENTES ===================================

const RECURRENTE_PREFS_KEY = "recurrentePrefs";

function loadRecurrentePrefs() {
  try {
    const raw = localStorage.getItem(RECURRENTE_PREFS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function RecurringPanel() {
  const prefs = loadRecurrentePrefs();
  const [form, setForm] = useState({
    sala: (prefs && prefs.sala) || SALAS[0],
    asunto: (prefs && prefs.asunto) || "",
    invitados: (prefs && prefs.invitados) || "",
    dias: (prefs && prefs.dias && prefs.dias.length > 0) ? prefs.dias : [1],
    horaInicio: (prefs && prefs.horaInicio) || "09:00",
    horaFin: (prefs && prefs.horaFin) || "10:00",
    fechaDesde: "",
    fechaHasta: "",
  });
  const [excepciones, setExcepciones] = useState([]);
  const [excepcionInput, setExcepcionInput] = useState("");
  const [preview, setPreview] = useState([]);
  const [previewPage, setPreviewPage] = useState(0);
  const [conflictKeys, setConflictKeys] = useState(new Set());
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const serieIdRef = useRef(null);

  // Recuerda sala/asunto/invitados/días/horas entre sesiones (NO las fechas,
  // que cambian cada vez). Evita re-teclear series parecidas cada semana.
  useEffect(() => {
    const { sala, asunto, invitados, dias, horaInicio, horaFin } = form;
    localStorage.setItem(
      RECURRENTE_PREFS_KEY,
      JSON.stringify({ sala, asunto, invitados, dias, horaInicio, horaFin })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sala, form.asunto, form.invitados, form.dias, form.horaInicio, form.horaFin]);

  // Tope de seguridad para no generar un lote absurdamente grande si alguien
  // elige una fecha "hasta" muy lejana.
  const MAX_OCURRENCIAS = 104;

  const toggleDia = (value) => {
    setForm((f) => {
      const dias = f.dias.includes(value)
        ? f.dias.filter((d) => d !== value)
        : [...f.dias, value].sort((a, b) => a - b);
      return { ...f, dias };
    });
  };

  const addExcepcion = () => {
    if (!excepcionInput) return;
    if (excepciones.includes(excepcionInput)) {
      setExcepcionInput("");
      return;
    }
    setExcepciones((e) => [...e, excepcionInput].sort());
    setExcepcionInput("");
  };
  const removeExcepcion = (fecha) =>
    setExcepciones((e) => e.filter((f) => f !== fecha));

  const build = useCallback(() => {
    if (!form.fechaDesde) {
      toast.warn("Elige la fecha de inicio.");
      return [];
    }
    if (!form.fechaHasta) {
      toast.warn("Elige hasta cuándo repetir la reserva.");
      return [];
    }
    if (form.dias.length === 0) {
      toast.warn("Elige al menos un día de la semana.");
      return [];
    }
    if (form.horaInicio >= form.horaFin) {
      toast.warn("La hora de inicio debe ser anterior a la de fin.");
      return [];
    }
    const base = new Date(form.fechaDesde + "T00:00:00");
    const limite = new Date(form.fechaHasta + "T00:00:00");
    if (limite < base) {
      toast.warn("La fecha hasta debe ser posterior a la fecha de inicio.");
      return [];
    }
    const excepcionesSet = new Set(excepciones);

    const out = [];
    for (const dia of form.dias) {
      const diff = (Number(dia) - base.getDay() + 7) % 7;
      const first = new Date(base);
      first.setDate(base.getDate() + diff);
      const d = new Date(first);
      while (d <= limite && out.length < MAX_OCURRENCIAS) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const fecha = `${yyyy}-${mm}-${dd}`;
        if (!excepcionesSet.has(fecha)) {
          out.push({
            reservacionId: genId(),
            sala: form.sala,
            asunto: form.asunto,
            invitados: form.invitados,
            fechaHoraInicio: `${fecha}T${form.horaInicio}:00`,
            fechaHoraFin: `${fecha}T${form.horaFin}:00`,
          });
        }
        d.setDate(d.getDate() + 7);
      }
    }
    out.sort((a, b) => a.fechaHoraInicio.localeCompare(b.fechaHoraInicio));
    if (out.length === 0) {
      toast.warn("No cae ningún día elegido entre esas dos fechas (revisa las excepciones).");
    }
    return out;
  }, [form, excepciones]);

  // Previsualiza Y valida choques reales contra la BD (dry-run del mismo
  // endpoint que guarda), para no descubrir el conflicto hasta dar "Crear".
  const onPreview = async () => {
    const lista = build();
    setPreview(lista);
    setPreviewPage(0);
    setConflictKeys(new Set());
    if (lista.length === 0) return;

    serieIdRef.current = genId();
    setCheckingConflicts(true);
    const res = await bulkSaveReservaciones(lista, {
      dryRun: true,
      serieId: serieIdRef.current,
    });
    setCheckingConflicts(false);
    if (res.ok) {
      toast.success("Sin choques: el lote se puede crear tal cual.");
    } else if (res.status === 409) {
      const conflicts = (res.data && res.data.conflicts) || [];
      setConflictKeys(new Set(conflicts.map((c) => c.reservacionId)));
      toast.warn(
        `${conflicts.length} de ${lista.length} reservación(es) chocan con algo existente (marcadas en rojo).`
      );
    } else {
      toast.error((res.data && res.data.message) || "Error al previsualizar.");
    }
  };

  const onSubmit = async () => {
    const lista = preview.length ? preview : build();
    if (lista.length === 0) return;
    if (conflictKeys.size > 0) {
      toast.warn("Resuelve los choques marcados en rojo antes de guardar.");
      return;
    }
    const serieId = serieIdRef.current || genId();
    const res = await bulkSaveReservaciones(lista, { serieId });
    if (res.ok) {
      toast.success(`${lista.length} reservación(es) creada(s).`);
      setPreview([]);
      setPreviewPage(0);
      setConflictKeys(new Set());
      serieIdRef.current = null;
    } else if (res.status === 409) {
      toast.error("Hay choques de horario; no se creó ninguna. Revisa los conflictos.");
      console.warn("Conflictos:", res.data && res.data.conflicts);
    } else {
      toast.error((res.data && res.data.message) || "Error al crear el lote.");
    }
  };

  const totalPreviewPages = Math.max(1, Math.ceil(preview.length / PAGE_SIZE));
  const pagedPreview = preview.slice(
    previewPage * PAGE_SIZE,
    previewPage * PAGE_SIZE + PAGE_SIZE
  );

  return (
    <div className="admin-card">
      <h5>Reserva recurrente</h5>
      <p className="admin-hint" style={{ marginTop: 0, marginBottom: 20 }}>
        Crea reservaciones repetidas (p. ej. todos los lunes y miércoles a la misma hora
        durante varias semanas). "Previsualizar" ya valida choques reales contra la BD
        (filas en rojo); al guardar, si algo choca no se crea ninguna del lote.
      </p>
      <div className="admin-grid admin-grid-3">
        <div>
          <label className="admin-label">Sala</label>
          <select
            className="admin-select"
            value={form.sala}
            onChange={(e) => setForm({ ...form, sala: e.target.value })}
          >
            {SALAS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="admin-label">Asunto</label>
          <input
            className="admin-input"
            value={form.asunto}
            onChange={(e) => setForm({ ...form, asunto: e.target.value })}
          />
        </div>
        <div>
          <label className="admin-label">Invitados (opcional, separados por coma)</label>
          <input
            className="admin-input"
            value={form.invitados}
            onChange={(e) => setForm({ ...form, invitados: e.target.value })}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="admin-label">Días de la semana</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
            {DIAS.map((d) => (
              <label
                key={d.value}
                style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}
              >
                <input
                  className="admin-check"
                  type="checkbox"
                  checked={form.dias.includes(d.value)}
                  onChange={() => toggleDia(d.value)}
                />
                {d.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="admin-label">Fecha de inicio</label>
          <input
            type="date"
            className="admin-input"
            value={form.fechaDesde}
            onChange={(e) => setForm({ ...form, fechaDesde: e.target.value })}
          />
        </div>
        <div>
          <label className="admin-label">Hora inicio</label>
          <input
            type="time"
            className="admin-input"
            value={form.horaInicio}
            onChange={(e) => setForm({ ...form, horaInicio: e.target.value })}
          />
        </div>
        <div>
          <label className="admin-label">Hora fin</label>
          <input
            type="time"
            className="admin-input"
            value={form.horaFin}
            onChange={(e) => setForm({ ...form, horaFin: e.target.value })}
          />
        </div>
        <div>
          <label className="admin-label">Repetir hasta</label>
          <input
            type="date"
            className="admin-input"
            min={form.fechaDesde || undefined}
            value={form.fechaHasta}
            onChange={(e) => setForm({ ...form, fechaHasta: e.target.value })}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="admin-label">Excepciones (fechas a saltar)</label>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              type="date"
              className="admin-input"
              style={{ maxWidth: 220 }}
              value={excepcionInput}
              onChange={(e) => setExcepcionInput(e.target.value)}
            />
            <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={addExcepcion}>
              Agregar
            </button>
          </div>
          {excepciones.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {excepciones.map((fecha) => (
                <span
                  key={fecha}
                  className="badge bg-body-tertiary text-dark px-3 py-2 shadow-sm position-relative"
                  style={{ borderRadius: 8 }}
                >
                  {fecha}
                  <button
                    type="button"
                    className="btn-close"
                    style={{ fontSize: 10, marginLeft: 8 }}
                    aria-label="Quitar"
                    onClick={() => removeExcepcion(fecha)}
                  ></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <button
          className="admin-btn admin-btn-ghost"
          style={{ marginRight: 10 }}
          onClick={onPreview}
          disabled={checkingConflicts}
        >
          {checkingConflicts ? "Revisando choques…" : "Previsualizar"}
        </button>
        <button
          className="admin-btn admin-btn-success"
          onClick={onSubmit}
          disabled={checkingConflicts || (preview.length > 0 && conflictKeys.size > 0)}
        >
          Crear reservaciones
        </button>
      </div>

      {preview.length > 0 && (
        <>
          <div className="admin-table-wrap" style={{ marginTop: 20 }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Sala</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {pagedPreview.map((p, i) => (
                  <tr
                    key={p.reservacionId}
                    className={conflictKeys.has(p.reservacionId) ? "admin-row-conflict" : ""}
                  >
                    <td>{previewPage * PAGE_SIZE + i + 1}</td>
                    <td>{p.sala}</td>
                    <td>{p.fechaHoraInicio.replace("T", " ")}</td>
                    <td>{p.fechaHoraFin.replace("T", " ")}</td>
                    <td>
                      {conflictKeys.has(p.reservacionId) ? (
                        <span>⚠ Choca con reservación existente</span>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={previewPage === 0}
              onClick={() => setPreviewPage((p) => p - 1)}
            >
              ← Anterior
            </button>
            <span className="admin-hint" style={{ margin: 0 }}>
              Página {previewPage + 1} de {totalPreviewPages} ({preview.length} reservación(es) en total
              {conflictKeys.size > 0 ? `, ${conflictKeys.size} con choque` : ""})
            </span>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              disabled={previewPage >= totalPreviewPages - 1}
              onClick={() => setPreviewPage((p) => p + 1)}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminDashboard;
